import { Button, Group, Notification, Portal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { API_BASE_URL } from '../api/client.js';
import { useAuth } from '../auth/useAuth.js';
import { useT } from '../i18n/useT.js';
import { rememberContext } from '../offline/enqueue.js';
import {
  applyUpdate,
  clearReferenceCache,
  onDrain,
  registerServiceWorker,
  startQueueDraining,
} from '../offline/register.js';

/**
 * Everything the PWA needs to happen once, at the top of the tree.
 *
 * A component rather than a call in `main.tsx`, because two of the three things
 * it does need React: the update prompt is a piece of UI, and the "your backlog
 * went through" toast needs the translation context. The third — registering
 * the worker — could live anywhere, and lives here so that all of it is in one
 * file rather than split across a module boundary for no reason.
 *
 * Renders nothing except the update prompt, and nothing at all most of the
 * time.
 */
export function PwaHost(): ReactNode {
  const t = useT();
  const { companyId } = useAuth();
  const [updateReady, setUpdateReady] = useState(false);
  const previousCompany = useRef<string | null>(companyId);

  /*
    Signing out clears the cached reference data. Detected as "there was a
    company and now there is none", which is what sign-out does and a company
    switch does not -- a switch goes from one id straight to another, and the
    cache is already keyed per company so it has no reason to be emptied.
  */
  useEffect(() => {
    if (previousCompany.current !== null && companyId === null) {
      void clearReferenceCache();
    }
    previousCompany.current = companyId;
  }, [companyId]);

  /*
    Register once, for the life of the tab.

    Deliberately not dependent on `companyId`: the worker is per-origin, not
    per-company, and re-registering on every company switch would churn the
    installation for no gain.
  */
  useEffect(() => {
    registerServiceWorker({
      onUpdateAvailable: () => {
        setUpdateReady(true);
      },
    });
  }, []);

  /*
    Tell the offline queue where to send things.

    The queue is drained by code that may run with no page open at all, so it
    cannot ask React where the API is or which company is selected. Both are
    written to IndexedDB whenever the switcher moves.
  */
  useEffect(() => {
    if (companyId === null) {
      return;
    }
    void rememberContext(API_BASE_URL, companyId);
  }, [companyId]);

  useEffect(() => {
    if (companyId === null) {
      return undefined;
    }
    return startQueueDraining();
  }, [companyId]);

  useEffect(
    () =>
      onDrain((result) => {
        if (result.sent > 0) {
          notifications.show({
            color: 'green',
            title: t('offline.sent', { count: result.sent }),
            message: '',
          });
        }
      }),
    [t],
  );

  if (!updateReady) {
    return null;
  }

  /*
    Asked, never taken.

    `registerType: 'prompt'` means a new worker waits rather than activating,
    and this is why: an automatic reload while somebody is halfway through
    logging a trip loses what they typed. The notification is dismissible and
    the old version keeps working until they choose.
  */
  return (
    <Portal>
      <Notification
        title={t('offline.updateAvailable')}
        onClose={() => {
          setUpdateReady(false);
        }}
        style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 400, maxWidth: 360 }}
      >
        <Stack gap="xs">
          <Text size="sm">{t('offline.updateHelp')}</Text>
          <Group>
            <Button
              size="compact-sm"
              onClick={() => {
                void applyUpdate();
              }}
            >
              {t('offline.update')}
            </Button>
          </Group>
        </Stack>
      </Notification>
    </Portal>
  );
}
